import { ArrowDown, ArrowUp, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";

import type { AnswerFeedbackEntry } from "@/lib/api";
import { cn } from "@/lib/utils";

import { highlight, type SortDir } from "./feedback-table";

interface Props {
  entries: AnswerFeedbackEntry[];
  searchTerm: string;
  sortDir: SortDir;
  onToggleSort: () => void;
  /** Whether a search or rating filter is active — picks the empty-state copy. */
  filtered: boolean;
  loading?: boolean;
}

/** Characters past which a comment collapses behind a "Show more" toggle. */
const COMMENT_CLAMP_CHARS = 120;

const COLUMN_COUNT = 6;

/** Full name when the profile has one, else the email, else the raw user id. */
function raterName(entry: AnswerFeedbackEntry): string {
  const name = [entry.first_name, entry.last_name].filter(Boolean).join(" ").trim();
  return name || entry.email || entry.user_id;
}

interface CommentCellProps {
  comment: string | null | undefined;
  searchTerm: string;
}

/** The rater's comment, clamped to two lines with the full text a click away. */
function CommentCell({ comment, searchTerm }: CommentCellProps) {
  const [expanded, setExpanded] = useState(false);
  const text = comment?.trim();
  if (!text) {
    return <span className="text-muted">—</span>;
  }
  const collapsible = text.length > COMMENT_CLAMP_CHARS || text.includes("\n");

  return (
    <div className="max-w-xs">
      <p
        className={cn(
          "break-words whitespace-pre-line",
          collapsible && !expanded && "line-clamp-2",
        )}
      >
        {highlight(text, searchTerm)}
      </p>
      {collapsible && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="text-primary text-xs font-medium hover:underline"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

/**
 * Teams' whole-answer ratings — "was this answer helpful?". Deliberately a
 * separate table from {@link FeedbackTable}, whose rows are per-part "was this
 * the right part?" ratings. Only `updated_at` is sortable server-side.
 */
export function AnswerFeedbackTable({
  entries,
  searchTerm,
  sortDir,
  onToggleSort,
  filtered,
  loading,
}: Props) {
  return (
    <div className="relative overflow-x-auto rounded-lg border">
      {loading && (
        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
          <span className="text-muted text-xs">Loading…</span>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-4 py-3 text-left font-medium">
              <button
                type="button"
                onClick={onToggleSort}
                className="hover:text-primary inline-flex items-center gap-1"
              >
                When
                {sortDir === "asc" ? (
                  <ArrowUp className="h-3 w-3" />
                ) : (
                  <ArrowDown className="h-3 w-3" />
                )}
              </button>
            </th>
            <th className="px-4 py-3 text-left font-medium">Rated by</th>
            <th className="px-4 py-3 text-left font-medium">Rating</th>
            <th className="px-4 py-3 text-left font-medium">Comment</th>
            <th className="px-4 py-3 text-left font-medium">Parts shown</th>
            <th className="px-4 py-3 text-left font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={COLUMN_COUNT} className="text-muted px-4 py-8 text-center">
                {filtered
                  ? "No answer ratings match these filters."
                  : "No Teams answer ratings yet"}
              </td>
            </tr>
          ) : (
            entries.map((entry) => (
              <tr key={entry.id} className="border-b align-top last:border-0 hover:bg-gray-50">
                <td className="text-muted px-4 py-3 whitespace-nowrap">
                  {new Date(entry.updated_at).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <div>{highlight(raterName(entry), searchTerm)}</div>
                  {entry.email && raterName(entry) !== entry.email && (
                    <div className="text-muted text-xs">{highlight(entry.email, searchTerm)}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {entry.is_helpful ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      <ThumbsUp className="h-3 w-3" />
                      Helpful
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-red-700">
                      <ThumbsDown className="h-3 w-3" />
                      Not helpful
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <CommentCell comment={entry.comment} searchTerm={searchTerm} />
                </td>
                <td className="px-4 py-3">
                  {entry.parts.length === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <ul className="space-y-1">
                      {entry.parts.map((part, i) => (
                        <li key={`${part.item_id}-${i}`}>
                          <span className="bg-accent-light text-primary inline-block rounded px-1.5 py-0.5 font-mono text-xs">
                            {highlight(part.item_id, searchTerm)}
                          </span>
                          {part.item_name && (
                            <div className="text-muted text-xs">{part.item_name}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="text-muted px-4 py-3 capitalize">{entry.source}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
